import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useGetBookingQuery, useCancelBookingMutation } from '../store/bookingsApi';
import { useUI } from '../context/UIContext';
import { ROUTES } from '../constants/routes';
import { Arrow } from '../components/home/Hero';
import {
  UI, wrapN, pheroIn, hM, hS, eyebrow, empty, panelCard, tile, input, spin,
  fieldLabel, req, chipLine, stInfo, stBad, btn, btnGhost, btnDanger, sw,
  alertBad, mono, num, textbtn,
} from '../styles/tl5';

// "03 Nov 2026" for the summary line.
const formatDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' });
};

const CANCEL_HOURS = 3;
const REASONS = [
  'Changed my mind',
  'Wrong slot picked',
  'Sample already collected elsewhere',
  'Health emergency',
  "Trustian didn't arrive",
  'Other',
];

// "6:00 AM" → { h: 6, m: 0 }
const parseSlot = (slot) => {
  const m = String(slot || '').trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (!m) return { h: 0, m: 0 };
  let h = Number(m[1]);
  const min = Number(m[2]);
  const ampm = (m[3] || '').toUpperCase();
  if (ampm === 'PM' && h < 12) h += 12;
  if (ampm === 'AM' && h === 12) h = 0;
  return { h, m: min };
};

const scheduledDateTime = (sched) => {
  if (!sched?.date) return null;
  const { h, m } = parseSlot(sched.slot);
  const [y, mo, d] = String(sched.date).split('-').map(Number);
  if (!y || !mo || !d) return null;
  return new Date(y, mo - 1, d, h, m);
};

const formatHours = (hrs) => {
  if (hrs <= 0) return 'past';
  const h = Math.floor(hrs);
  const m = Math.round((hrs - h) * 60);
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

const CancelBooking = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useUI();

  const { data, isLoading, isError } = useGetBookingQuery(id, { skip: !id });
  const booking = data?.booking;

  const [cancelBooking, { isLoading: cancelling }] = useCancelBookingMutation();

  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [needBank, setNeedBank] = useState(false);
  const [bank, setBank] = useState({ account_holder: '', account_number: '', ifsc: '', bank_name: '' });
  const [errMsg, setErrMsg] = useState('');

  const eligibility = useMemo(() => {
    if (!booking) return { ok: false, reason: '', hoursLeft: 0 };
    if (booking.status === 'cancelled') return { ok: false, reason: 'This booking is already cancelled.' };
    if (['collected', 'reported'].includes(booking.status)) {
      return { ok: false, reason: `This booking is already ${booking.status} — too late to cancel.` };
    }
    const sched = scheduledDateTime(booking.schedule);
    if (!sched) return { ok: true, hoursLeft: Infinity };
    const hoursLeft = (sched.getTime() - Date.now()) / 3_600_000;
    if (hoursLeft < CANCEL_HOURS) {
      return {
        ok: false,
        hoursLeft,
        // `formatHours` returns 'past' for a slot that has gone, which read
        // as "Your slot is in past." — so that case gets its own sentence.
        reason: hoursLeft <= 0
          ? 'That slot has already passed, so this booking can no longer be cancelled here.'
          : `Cancellations are allowed only up to ${CANCEL_HOURS} hours before your slot. Yours is in ${formatHours(hoursLeft)}.`,
      };
    }
    return { ok: true, hoursLeft };
  }, [booking]);

  if (isLoading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
        <Box sx={{ ...spin, width: 28, height: 28, borderWidth: 3 }} />
      </Box>
    );
  }

  if (isError || !booking) {
    return (
      <Box component="section" sx={{ py: 'clamp(32px, 3.6vw, 56px)' }}>
        <Box sx={{ ...wrapN, ...empty }}>
          <Typography sx={{ fontSize: 32, lineHeight: 1 }}>⚠️</Typography>
          <Typography component="h1" sx={hS}>Booking not found</Typography>
          <Typography sx={{ fontFamily: UI, fontSize: 15, color: 'var(--tl-muted)' }}>
            We couldn&rsquo;t load booking {id}.
          </Typography>
          <Box component="button" type="button" onClick={() => navigate(ROUTES.DASHBOARD)} sx={{ ...btn, mt: '6px' }}>
            Back to bookings
            <Arrow size={16} />
          </Box>
        </Box>
      </Box>
    );
  }

  const wasPaid = booking.payment?.status === 'paid';
  const total = Number(booking.amounts?.total || 0);

  const onConfirm = async () => {
    setErrMsg('');
    if (!reason) return setErrMsg('Please pick a reason');
    if (needBank) {
      const { account_holder, account_number, ifsc } = bank;
      if (!account_holder.trim() || !account_number.trim() || !ifsc.trim()) {
        return setErrMsg('Please fill in account holder, account number and IFSC');
      }
    }
    try {
      const res = await cancelBooking({
        bookingId: booking.booking_id,
        reason,
        notes: notes.trim() || undefined,
        bank_details: needBank ? bank : undefined,
      }).unwrap();
      const refundStatus = res?.booking?.refund?.status;
      const msg =
        refundStatus === 'auto'
          ? 'Booking cancelled. Your refund has been queued — it should reflect within a few hours.'
          : refundStatus === 'manual'
            ? 'Booking cancelled. Our team will process the refund manually within 2–3 working days.'
            : 'Booking cancelled.';
      showToast(msg);
      navigate(ROUTES.DASHBOARD);
    } catch (err) {
      setErrMsg(err?.data?.message || 'Could not cancel the booking. Please try again.');
    }
  };

  // Not eligible (already cancelled, too late, already collected) — the
  // reference shows this as its own screen rather than a disabled form.
  if (!eligibility.ok) {
    return (
      <Box component="section" sx={{ py: 'clamp(32px, 3.6vw, 56px)' }}>
        <Box sx={{ ...wrapN, ...empty }}>
          <Box component="span" sx={stBad}>Cannot cancel</Box>
          <Typography component="h1" sx={{ ...hS, mt: '6px' }}>
            Booking <Box component="span" sx={mono}>{booking.booking_id}</Box>
          </Typography>
          <Typography sx={{ fontFamily: UI, fontSize: 15, lineHeight: 1.6, color: 'var(--tl-muted)', maxWidth: 420 }}>
            {eligibility.reason}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px', mt: '6px' }}>
            <Box
              component="button"
              type="button"
              onClick={() => navigate(`${ROUTES.TRACK}?id=${encodeURIComponent(booking.booking_id)}`)}
              sx={btn}
            >
              Track this booking
              <Arrow size={16} />
            </Box>
            <Box component="button" type="button" onClick={() => navigate(ROUTES.DASHBOARD)} sx={btnGhost}>
              My bookings
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box component="section">
        <Box sx={{ ...wrapN, ...pheroIn, gap: '12px' }}>
          <Box
            component="button"
            type="button"
            onClick={() => navigate(`${ROUTES.TRACK}?id=${encodeURIComponent(booking.booking_id)}`)}
            sx={{ ...textbtn, display: 'inline-flex', alignItems: 'center', gap: '8px', alignSelf: 'flex-start', px: 0, color: 'var(--tl-g9)' }}
          >
            <Arrow size={16} dir="left" />
            Back to Track
          </Box>

          <Box component="span" sx={{ ...eyebrow, background: '#FEE4E2', color: '#B42318' }}>
            Cancel booking
          </Box>

          <Typography component="h1" sx={hM}>
            Cancel <Box component="span" sx={mono}>{booking.booking_id}</Box>?
          </Typography>
        </Box>
      </Box>

      <Box component="section" sx={{ pb: 'clamp(32px, 3.6vw, 56px)' }}>
        <Box sx={{ ...wrapN, display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* ── what is being cancelled ── */}
          <Box sx={panelCard}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
              <Box component="span" sx={stInfo}>Scheduled</Box>
              <Box component="span" sx={{ ...num, fontFamily: UI, fontSize: 14.5, color: 'var(--tl-muted)' }}>
                {formatDate(booking.schedule?.date)}
                {booking.schedule?.slot ? ` · ${booking.schedule.slot}` : ''}
              </Box>
            </Box>

            {(booking.items || []).length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', mt: '14px' }}>
                {booking.items.map((it) => (
                  <Box component="span" key={`${it.item_type}-${it.item_id}`} sx={{ ...chipLine, height: 'auto', minHeight: 30, py: '6px', whiteSpace: 'normal', lineHeight: 1.35 }}>
                    {it.name}
                  </Box>
                ))}
              </Box>
            )}

            {wasPaid && total > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px', mt: '16px', pt: '14px', borderTop: '1px solid var(--tl-line)' }}>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 14.5, color: 'var(--tl-muted)' }}>Paid</Box>
                <Box component="span" sx={{ ...num, fontFamily: UI, fontSize: 18, fontWeight: 800, color: 'var(--tl-g9)' }}>
                  ₹{total.toLocaleString('en-IN')}
                </Box>
              </Box>
            )}
          </Box>

          {/* ── the form ── */}
          <Box sx={panelCard}>
            <Typography component="h2" sx={hS}>Tell us why you&rsquo;re cancelling</Typography>
            <Typography sx={{ fontFamily: UI, fontSize: 15, color: 'var(--tl-muted)', mt: '6px', mb: '20px' }}>
              This helps us fix what went wrong.
            </Typography>

            {errMsg && <Box sx={{ ...alertBad, mb: '18px' }} role="alert">{errMsg}</Box>}

            <Box sx={{ mb: '18px' }}>
              <Box component="label" htmlFor="cx-reason" sx={fieldLabel}>
                Reason<Box component="span" sx={req}> *</Box>
              </Box>
              <Box
                component="select"
                id="cx-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                sx={{ ...input, cursor: 'pointer' }}
              >
                <option value="">— Select a reason —</option>
                {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </Box>
            </Box>

            <Box sx={{ mb: '18px' }}>
              <Box component="label" htmlFor="cx-notes" sx={fieldLabel}>
                Help us improve — what went wrong?
              </Box>
              <Box
                component="textarea"
                id="cx-notes"
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Anything else we should know?"
                sx={{ ...input, height: 'auto', minHeight: 110, py: '14px', resize: 'vertical', lineHeight: 1.55 }}
              />
            </Box>

            {/* Bank refund — only meaningful when money actually moved. */}
            {wasPaid && (
              <Box sx={{ ...tile, p: '18px 20px', mb: '20px' }}>
                <Box component="label" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', cursor: 'pointer' }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ fontFamily: UI, fontSize: 16, fontWeight: 700, color: 'var(--tl-g9)' }}>
                      Refund to a bank account instead
                    </Box>
                    <Box sx={{ fontFamily: UI, fontSize: 14, color: 'var(--tl-muted)', mt: '2px' }}>
                      Use this if the original payment method can&rsquo;t receive a refund.
                    </Box>
                  </Box>
                  <Box component="span" sx={sw}>
                    <input
                      type="checkbox"
                      checked={needBank}
                      onChange={(e) => setNeedBank(e.target.checked)}
                      aria-label="Refund to a bank account instead"
                    />
                  </Box>
                </Box>

                {needBank && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: '14px', mt: '18px' }}>
                    {[
                      ['account_holder', 'Account holder', true, 2],
                      ['account_number', 'Account number', true, 1],
                      ['ifsc', 'IFSC', true, 1],
                      ['bank_name', 'Bank name', false, 2],
                    ].map(([key, label, required, span]) => (
                      <Box key={key} sx={{ gridColumn: { xs: '1 / -1', sm: span === 2 ? '1 / -1' : 'auto' } }}>
                        <Box component="label" htmlFor={`cx-${key}`} sx={fieldLabel}>
                          {label}{required && <Box component="span" sx={req}> *</Box>}
                        </Box>
                        <Box
                          component="input"
                          id={`cx-${key}`}
                          value={bank[key]}
                          onChange={(e) => setBank({ ...bank, [key]: e.target.value })}
                          sx={{ ...input, ...(key === 'ifsc' || key === 'account_number' ? mono : null) }}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
              <Box
                component="button"
                type="button"
                onClick={() => navigate(`${ROUTES.TRACK}?id=${encodeURIComponent(booking.booking_id)}`)}
                sx={btnGhost}
              >
                Keep the booking
              </Box>
              <Box component="button" type="button" onClick={onConfirm} disabled={cancelling} sx={btnDanger}>
                {cancelling && <Box component="span" sx={{ ...spin, width: 16, height: 16, borderWidth: 2 }} />}
                {cancelling ? 'Cancelling…' : 'Confirm cancellation'}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CancelBooking;
